import {
  BottomSheet,
  DialogFooter,
  Illustration,
  KeyValueList,
  KeyValueListItem,
  Text,
  TextButton,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, ThemeProvider } from "@mui/material";
import { PasswordDisplay } from "@src/appV2/redesign/Charting/PasswordDisplay";
import { Button } from "@src/appV2/redesign/components/Button";
import { type ChartingCredential } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { Link } from "react-router-dom";

import { BottomSheetHeader } from "../components/BottomSheetHeader";

export interface YourChartingLoginBottomSheetProps {
  modalState: UseModalState;
  chartingCredentials?: ChartingCredential;
  onGotIt?: () => void;
  onUpdateChartingLogin?: () => void;
  onClose?: () => void;
}

export function YourChartingLoginBottomSheet({
  modalState,
  chartingCredentials,
  onGotIt,
  onUpdateChartingLogin,
  onClose,
}: YourChartingLoginBottomSheetProps) {
  const handleGotIt = () => {
    onGotIt?.();
    modalState.closeModal();
    onClose?.();
  };

  const handleUpdateChartingLogin = () => {
    onUpdateChartingLogin?.();
    modalState.closeModal();
    onClose?.();
  };

  const handleClose = () => {
    modalState.closeModal();
    onClose?.();
  };

  if (!isDefined(chartingCredentials)) {
    return null;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <BottomSheet
        canDismiss
        modalState={modalState}
        header={<BottomSheetHeader onClose={handleClose} />}
        onClose={handleClose}
      >
        <Box sx={{ paddingX: 5, display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Illustration type="quiz-required" />
          </Box>
          <Text bold variant="h3" sx={{ textAlign: "center" }}>
            Your charting login
          </Text>

          <Text
            variant="body1"
            sx={{ marginTop: 6, paddingX: 2, textAlign: "center", textWrap: "balance" }}
          >
            Use the credentials below to log in to the EHR
          </Text>

          {isDefined(chartingCredentials) &&
            isDefined(chartingCredentials.username) &&
            isDefined(chartingCredentials.password) && (
              <Box sx={{ marginTop: 6 }}>
                <KeyValueList>
                  <KeyValueListItem
                    key="username"
                    label="Username"
                    value={chartingCredentials.username}
                  />
                  <KeyValueListItem
                    key="password"
                    label="Password"
                    value={<PasswordDisplay password={chartingCredentials.password} />}
                  />
                </KeyValueList>
              </Box>
            )}
        </Box>

        <DialogFooter sx={{ marginTop: 8 }} orientation="vertical" LinkComponent={Link}>
          <Button fullWidth size="large" onClick={handleGotIt}>
            Got it
          </Button>

          <TextButton size="large" LinkComponent={Link} onClick={handleUpdateChartingLogin}>
            Update charting login
          </TextButton>
        </DialogFooter>
      </BottomSheet>
    </ThemeProvider>
  );
}
